import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ConnectionTest = () => {
  const [connectionStatus, setConnectionStatus] = useState({});
  const [testing, setTesting] = useState(false);
  const [lastTest, setLastTest] = useState(null);

  const testConnections = [
    {
      name: 'Backend API',
      description: 'Conexão com servidor principal',
      endpoint: '/api/',
      type: 'backend'
    },
    {
      name: 'Configurações de API',
      description: 'Status das integrações de afiliados',
      endpoint: '/api/integrations/status',
      type: 'integrations'
    },
    {
      name: 'Sistema de Tracking',
      description: 'Sistema de rastreamento em tempo real',
      endpoint: '/api/tracking/performance/test_user',
      type: 'tracking'
    },
    {
      name: 'Conectividade Externa',
      description: 'Teste de conectividade com internet',
      endpoint: 'https://httpbin.org/get',
      type: 'external',
      external: true
    }
  ];

  useEffect(() => {
    runConnectionTests();
  }, []);

  const runConnectionTests = async () => {
    setTesting(true);
    const results = {};

    for (const test of testConnections) {
      try {
        const startTime = Date.now();
        
        let response;
        if (test.external) {
          response = await axios.get(test.endpoint, { timeout: 10000 });
        } else {
          response = await axios.get(`${API_URL}${test.endpoint}`, { timeout: 10000 });
        }
        
        const responseTime = Date.now() - startTime;
        
        results[test.type] = {
          status: 'success',
          responseTime,
          statusCode: response.status,
          message: 'Conectado com sucesso',
          timestamp: new Date().toISOString()
        };

      } catch (error) {
        results[test.type] = {
          status: 'error',
          responseTime: 0,
          statusCode: error.response?.status || 0,
          message: error.response?.data?.detail || error.message || 'Conexão falhou',
          timestamp: new Date().toISOString()
        };
      }
    }

    setConnectionStatus(results);
    setLastTest(new Date());
    setTesting(false);
    
    // Show summary toast
    const successCount = Object.values(results).filter(r => r.status === 'success').length;
    const totalTests = testConnections.length;
    
    if (successCount === totalTests) {
      toast.success(`✅ Todas as conexões funcionando! (${successCount}/${totalTests})`);
    } else {
      toast.warning(`⚠️ ${successCount}/${totalTests} conexões funcionando`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'text-green-600 bg-green-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      default:
        return '⏳';
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Teste de Conectividade</h1>
        <p className="text-gray-600 mt-2">
          Verifique o status de todas as conexões da aplicação
        </p>
        {lastTest && (
          <p className="text-sm text-gray-500 mt-1">
            Último teste: {lastTest.toLocaleString('pt-BR')}
          </p>
        )}
      </div>

      {/* Test Controls */}
      <div className="mb-6 bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Status Geral</h2>
            <p className="text-sm text-gray-600">
              Teste todas as conexões para verificar funcionalidade
            </p>
          </div>
          <button
            onClick={runConnectionTests}
            disabled={testing}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {testing ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Testando...</span>
              </>
            ) : (
              <>
                <span>🔄</span>
                <span>Testar Conexões</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Connection Tests Results */}
      <div className="space-y-4">
        {testConnections.map((test) => {
          const result = connectionStatus[test.type];
          
          return (
            <div key={test.type} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <span className="text-2xl">
                      {result ? getStatusIcon(result.status) : '⏳'}
                    </span>
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {test.name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {test.description}
                      </p>
                      <p className="text-xs text-gray-500 font-mono">
                        {test.external ? test.endpoint : `${API_URL}${test.endpoint}`}
                      </p>
                    </div>
                  </div>
                  
                  {result && (
                    <div className="mt-3 ml-11">
                      <div className="flex items-center space-x-4">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${getStatusColor(result.status)}`}>
                          {result.status === 'success' ? 'Conectado' : 'Erro'}
                        </span>
                        
                        {result.responseTime > 0 && (
                          <span className="text-sm text-gray-600">
                            Tempo: {result.responseTime}ms
                          </span>
                        )}
                        
                        {result.statusCode > 0 && (
                          <span className="text-sm text-gray-600">
                            Status: {result.statusCode}
                          </span>
                        )}
                      </div>
                      
                      <p className={`text-sm mt-2 ${
                        result.status === 'success' ? 'text-green-700' : 'text-red-700'
                      }`}>
                        {result.message}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Network Info */}
      <div className="mt-8 bg-blue-50 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-blue-900 mb-4">
          💡 Informações sobre Conectividade
        </h2>
        <div className="space-y-3 text-blue-800">
          <p className="text-sm">
            <strong>✅ Conexão com Internet:</strong> A aplicação utiliza a conexão de internet do seu dispositivo automaticamente.
          </p>
          <p className="text-sm">
            <strong>🔌 APIs de Afiliados:</strong> As conexões com ClickBank, Hotmart, Monetizze e Amazon dependem das suas chaves de API configuradas.
          </p>
          <p className="text-sm">
            <strong>📊 Tracking em Tempo Real:</strong> O sistema monitora cliques e conversões automaticamente quando as APIs estão configuradas.
          </p>
          <p className="text-sm">
            <strong>💰 Pagamentos Automáticos:</strong> PIX, Stripe e PayPal processam comissões automaticamente conforme configurado.
          </p>
        </div>
      </div>

      {/* Auto-refresh */}
      <div className="mt-6 text-center">
        <button
          onClick={runConnectionTests}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          🔄 Testar Novamente
        </button>
      </div>
    </div>
  );
};

export default ConnectionTest;